import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader } from "@/mockups/ui";
import { toast } from "sonner";
import { ShieldCheck, UserPlus, Clock, ChevronRight, History, ArrowRightLeft } from "lucide-react";
import { fmtHM } from "@/lib/utils";

const ROLES = ["Administrador", "Directora", "Administración", "Cuentas", "Diseñador"];
const COST_PROFILES = ["Socio", "Director", "Head", "Técnico"];
export const ROLE_COLORS = {
  "Administrador": "bg-primary/12 text-primary",
  "Directora": "bg-chart-4/12 text-chart-4",
  "Administración": "bg-gold/15 text-[#a9741c] dark:text-gold",
  "Cuentas": "bg-chart-4/12 text-chart-4",
  "Diseñador": "bg-muted text-muted-foreground",
};
const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("");
const euro = (n) => (n == null ? "—" : Number(n).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));
const fmt = (d) => (d ? new Date(d).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "2-digit" }) : "—");

function InviteDialog({ open, onOpenChange, onDone }) {
  const [form, setForm] = useState({ email: "", name: "", role: "Diseñador", job_title: "", cost_profile: "Técnico" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!form.email.trim() || !form.email.includes("@")) { toast.error("Introduce un email válido"); return; }
    setSaving(true);
    try {
      await api.post("/users/invite", form);
      toast.success("Persona añadida. Recibirá su rol al entrar con Google.");
      onOpenChange(false);
      setForm({ email: "", name: "", role: "Diseñador", job_title: "", cost_profile: "Técnico" });
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Añadir persona</DialogTitle>
          <DialogDescription>Se creará la ficha con su rol y perfil de coste. Al iniciar sesión con Google por primera vez con este email, se le aplicará automáticamente.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1"><Label className="text-xs">Email (Google Workspace)</Label>
            <Input type="email" placeholder="nombre@grabarte360.com" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="invite-email" /></div>
          <div className="space-y-1"><Label className="text-xs">Nombre</Label>
            <Input placeholder="Nombre y apellidos" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="invite-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Rol de acceso</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-xs">Perfil de coste</Label>
              <Select value={form.cost_profile} onValueChange={(v) => set("cost_profile", v)}>
                <SelectTrigger data-testid="invite-cost"><SelectValue /></SelectTrigger>
                <SelectContent>{COST_PROFILES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Puesto</Label>
            <Input placeholder="Ej: Dir. Producción, Copy…" value={form.job_title} onChange={(e) => set("job_title", e.target.value)} data-testid="invite-job" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="invite-submit">{saving ? "Añadiendo…" : "Añadir"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExEmpleados({ activeMembers }) {
  const { data, loading, refetch } = useFetch("/team/unassigned-hours");
  const rows = data?.rows || [];
  const reassign = async (name, user_id) => {
    if (!user_id) return;
    try {
      const res = await api.post("/team/unassigned-hours/reassign", { name, to_user_id: user_id });
      toast.success(`${res.data.reassigned} registros reasignados`);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al reasignar"); }
  };
  return (
    <Card className="border-border shadow-none overflow-hidden" data-testid="ex-empleados-card">
      <div className="px-5 py-4 border-b border-border flex items-start gap-3">
        <div className="h-9 w-9 rounded-md bg-muted flex items-center justify-center shrink-0"><History className="h-4 w-4 text-muted-foreground" /></div>
        <div>
          <div className="font-medium text-sm">Horas de ex-empleados y sin asignar</div>
          <div className="text-xs text-muted-foreground mt-0.5">Horas importadas de Teamleader que no están vinculadas a un usuario activo. Se conservan como histórico; puedes reasignarlas a un usuario actual.</div>
        </div>
      </div>
      {loading ? (
        <div className="py-16 flex justify-center"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground" data-testid="ex-empleados-empty">No hay horas sin asignar. Todo está vinculado a usuarios activos.</div>
      ) : (
        <div className="overflow-x-auto">
          <Table className="min-w-[820px]">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5">Persona (Teamleader)</TableHead>
                <TableHead className="text-right">Horas</TableHead>
                <TableHead className="text-right">Registros</TableHead>
                <TableHead>Proyectos</TableHead>
                <TableHead>Rango</TableHead>
                <TableHead className="text-right pr-5">Reasignar a</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.name} data-testid={`ex-row-${r.name}`}>
                  <TableCell className="pl-5 font-medium text-sm">{r.name}</TableCell>
                  <TableCell className="text-right tabular-nums text-sm">{fmtHM(r.hours)}</TableCell>
                  <TableCell className="text-right tabular-nums text-sm text-muted-foreground">{r.entries}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {r.projects_count} {r.top_projects?.length ? <span className="text-xs">· {r.top_projects.join(", ")}{r.projects_count > r.top_projects.length ? "…" : ""}</span> : null}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground tabular-nums">{fmt(r.first_date)} → {fmt(r.last_date)}</TableCell>
                  <TableCell className="text-right pr-5">
                    <Select onValueChange={(v) => reassign(r.name, v)}>
                      <SelectTrigger className="h-8 w-[180px] ml-auto" data-testid={`ex-reassign-${r.name}`}><SelectValue placeholder="Asignar a…" /></SelectTrigger>
                      <SelectContent>{activeMembers.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}

export default function Equipo() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, loading, refetch } = useFetch("/team/members");
  const [showInvite, setShowInvite] = useState(false);

  const list = data?.members || [];
  const access = data?.access || {};
  const activeMembers = list.filter((m) => m.is_active);

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="equipo-page">
      <PageHeader title="Equipo" subtitle="Cada persona es un trabajador con su ficha, acceso, coste, vacaciones, nóminas y proyectos en un solo sitio">
        {access.can_invite && <Button size="sm" className="gap-1.5" onClick={() => setShowInvite(true)} data-testid="add-person-btn"><UserPlus className="h-4 w-4" /> Añadir persona</Button>}
        {user?.role === "Administrador" && <Link to="/permisos"><Button variant="outline" size="sm" className="gap-1.5" data-testid="go-permissions-btn"><ShieldCheck className="h-4 w-4" /> Permisos</Button></Link>}
      </PageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Miembros", value: list.length },
          { label: "Activos", value: activeMembers.length },
          { label: "Pendientes de acceso", value: list.filter((u) => u.pending_login).length },
          { label: "Administradores", value: list.filter((u) => u.role === "Administrador").length },
        ].map((s, i) => (
          <Card key={i} className="border-border shadow-none p-4"><div className="font-heading font-bold text-2xl tabular-nums">{s.value}</div><div className="text-xs text-muted-foreground mt-1">{s.label}</div></Card>
        ))}
      </div>

      <Tabs defaultValue="equipo">
        <TabsList data-testid="equipo-tabs">
          <TabsTrigger value="equipo" data-testid="tab-equipo">Equipo</TabsTrigger>
          {access.econ && <TabsTrigger value="ex" data-testid="tab-ex-empleados"><ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" /> Ex-empleados / sin asignar</TabsTrigger>}
        </TabsList>

        <TabsContent value="equipo" className="mt-4">
          <Card className="border-border shadow-none overflow-hidden">
            {loading ? <div className="py-16 flex justify-center"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div> : (
              <div className="overflow-x-auto">
                <Table className="min-w-[900px]">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="pl-5">Trabajador</TableHead>
                      <TableHead>Rol de acceso</TableHead>
                      <TableHead>Puesto</TableHead>
                      <TableHead>Contrato</TableHead>
                      <TableHead className="text-center">Vac.</TableHead>
                      <TableHead className="text-center">Proyectos</TableHead>
                      <TableHead className="text-right">Horas</TableHead>
                      {access.econ && <TableHead className="text-right">Coste/h</TableHead>}
                      <TableHead className="w-8"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {list.map((u) => (
                      <TableRow key={u.user_id} className="cursor-pointer group" onClick={() => navigate(`/rrhh/equipo/${u.user_id}`)} data-testid={`member-row-${u.user_id}`}>
                        <TableCell className="pl-5">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9"><AvatarImage src={u.picture} alt={u.name} /><AvatarFallback className="text-[11px] bg-muted">{initials(u.name)}</AvatarFallback></Avatar>
                            <div className="min-w-0">
                              <span className="font-medium text-sm block truncate">{u.name}</span>
                              {u.pending_login ? <span className="inline-flex items-center gap-1 text-[11px] text-gold"><Clock className="h-3 w-3" /> Pendiente de 1er acceso</span>
                                : !u.is_active ? <span className="text-[11px] text-muted-foreground">Inactivo</span>
                                : <span className="text-[11px] text-muted-foreground truncate block">{u.email}</span>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell><Badge variant="outline" className={`border-transparent font-medium ${ROLE_COLORS[u.role]}`}>{u.role}</Badge></TableCell>
                        <TableCell className="text-sm text-muted-foreground">{u.job_title || u.position || "—"}</TableCell>
                        <TableCell>{u.contract_type ? <Badge variant="secondary" className="font-normal">{u.contract_type}</Badge> : <span className="text-sm text-muted-foreground">—</span>}</TableCell>
                        <TableCell className="text-center text-sm tabular-nums">{u.vacation_available}<span className="text-muted-foreground">/{u.vacation_days_year}</span></TableCell>
                        <TableCell className="text-center text-sm tabular-nums">{u.projects_count}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{u.hours_total ? fmtHM(u.hours_total) : "—"}</TableCell>
                        {access.econ && <TableCell className="text-right text-sm tabular-nums">{u.hourly_cost != null ? `${euro(u.hourly_cost)}/h` : "—"}</TableCell>}
                        <TableCell><ChevronRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-muted-foreground" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </TabsContent>

        {access.econ && (
          <TabsContent value="ex" className="mt-4">
            <ExEmpleados activeMembers={activeMembers} />
          </TabsContent>
        )}
      </Tabs>

      <InviteDialog open={showInvite} onOpenChange={setShowInvite} onDone={refetch} />
    </div>
  );
}
